import { useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useRecentPatients } from '@/hooks/useRecentPatients'

const CORAL = '#e8624a'
const BAR_HEIGHT_PX = 38

function initialsFor(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Horizontal "RECENT" chip + patient pill row, rendered immediately below
 * the top app header. Returns `null` when no patients have been viewed yet —
 * keeps vertical space unused on first paint.
 *
 * Styling mirrors the mockup `ChartPage.tsx:846-899` in `packages/ui-designs/`:
 * subtle gray fill (`#f9fafb`) on a `1.5px solid #e5e7eb` outline for inactive
 * pills, coral border + tint for the active patient.
 */
export function RecentPatientsBar() {
  const { recent } = useRecentPatients()
  const navigate = useNavigate()
  const location = useLocation()
  // Match `/patients/<id>` or `/patients/<id>/<anything>` (e.g. `/demographics`,
  // `/<docRefId>/progress-notes`). Mirrors the regex used in `Shell.tsx` so the
  // active-patient highlight stays in sync across nested routes.
  const patientMatch = location.pathname.match(/^\/patients\/([^/]+)/)
  const activePatientId = patientMatch?.[1] ?? null

  // Publish the bar's vertical footprint onto the root element so the
  // <Shell> main padding can compensate without prop drilling. Mirrors the
  // `--zoom-panel-width` pattern in `ZoomPanel.tsx`. useLayoutEffect (not
  // useEffect) so the var is set BEFORE paint — otherwise there's a frame
  // where `--recent-bar-height` is 0, the portaled patient header collapses
  // to top:64px under this z-99 bar, and the bar clips the header's subtitle.
  useLayoutEffect(() => {
    const root = document.documentElement
    if (recent.length === 0) {
      root.style.removeProperty('--recent-bar-height')
      return
    }
    root.style.setProperty('--recent-bar-height', `${BAR_HEIGHT_PX}px`)
    return () => {
      root.style.removeProperty('--recent-bar-height')
    }
  }, [recent.length])

  if (recent.length === 0) return null

  return (
    <div
      aria-label="Recent patients"
      className="fixed top-[64px] left-0 z-99 w-full"
      style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        height: BAR_HEIGHT_PX,
        flexShrink: 0,
        overflowX: 'auto',
      }}
    >
      <span
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: '#9ca3af',
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          marginRight: 8,
          whiteSpace: 'nowrap',
        }}
      >
        Recent
      </span>
      {recent.map((patient, index) => {
        const isActive = patient.id === activePatientId
        const initials = initialsFor(patient.name)
        return (
          <div key={patient.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {index > 0 && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#d1d5db"
                strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
            <button
              type="button"
              onClick={() => navigate(`/patients/${patient.id}`)}
              aria-current={isActive ? 'page' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px 4px 6px',
                borderRadius: 999,
                border: isActive ? `1.5px solid ${CORAL}` : '1.5px solid #e5e7eb',
                background: isActive ? 'rgba(232,98,74,0.06)' : '#f9fafb',
                cursor: 'pointer',
                fontSize: 12.5,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? CORAL : '#374151',
                whiteSpace: 'nowrap',
                transition: 'all 0.12s',
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: isActive ? CORAL : '#d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                {initials}
              </span>
              {patient.name}
            </button>
          </div>
        )
      })}
    </div>
  )
}
